import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  Tooltip,
  Divider,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import DashboardIcon from "@mui/icons-material/Dashboard";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import SecurityIcon from "@mui/icons-material/Security";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import HistoryIcon from "@mui/icons-material/History";
import StorageIcon from "@mui/icons-material/Storage";
import SyncIcon from "@mui/icons-material/Sync";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import hxLogo from "../assets/hx-logo.svg";
import { useAuth } from "contexts/AuthContext";
import { canAccess } from "utils/roleAccess";

// Logo component
const Logo: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: 47,
      marginBottom: "10px",
      backgroundColor: "#0182c0",
    }}
  >
    <img
      src={hxLogo}
      alt="HX Logo"
      style={{
        height: "42px",
        width: "auto",
      }}
    />
  </Box>
);

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
}

// NavItem component
const NavItem: React.FC<NavItemProps> = ({ to, icon, label, isActive }) => (
  <Tooltip title={label} placement="right">
    <ListItem
      component={Link}
      to={to}
      sx={{
        py: 1.5,
        display: "flex",
        justifyContent: "center",
        color: "white",
        backgroundColor: isActive ? "rgba(255, 255, 255, 0.1)" : "transparent",
        borderLeft: isActive ? "4px solid #ffffff" : "4px solid transparent",
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.1)",
        },
      }}
    >
      <ListItemIcon
        sx={{ minWidth: 0, color: "white", justifyContent: "center" }}
      >
        {icon}
      </ListItemIcon>
    </ListItem>
  </Tooltip>
);

// LeftNav component
const LeftNav: React.FC = () => {
  const location = useLocation();
  const { auth } = useAuth();
  const user = auth.user;
  const showAccounts = canAccess(user, 'accounts');
  const showDashboards = canAccess(user, 'dashboards');
  const showHxcPartners = canAccess(user, 'hxc-partners');
  const showCompSets = canAccess(user, 'comp-sets');
  const showAccountAccess = canAccess(user, 'data-access-control');
  const showProgramAccess = canAccess(user, 'program-access');
  const showChangeLog = canAccess(user, 'change-log');
  const showMasterData = canAccess(user, 'master-data');
  const showAlerts = canAccess(user, 'alerts');
  const showParticipants = canAccess(user, 'participants');

  return (
    <Box
      sx={{
        width: 60,
        backgroundColor: "#0182c0",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 1200,
      }}
    >
      <Logo />
      <List sx={{ p: 0, mt: 0 }} component="nav">
        {showAccounts && (
          <NavItem
            to="/accounts"
            icon={<BusinessIcon />}
            label="Accounts"
            isActive={
              location.pathname === "/accounts" ||
              location.pathname === "/" ||
              location.pathname.startsWith("/accounts")
            }
          />
        )}
        {showDashboards && (
          <NavItem
            to="/dashboards"
            icon={<DashboardIcon />}
            label="Dashboards"
            isActive={
              location.pathname === "/dashboards" ||
              location.pathname.startsWith("/dashboards")
            }
          />
        )}
        {showHxcPartners && (
          <NavItem
            to="/hxc-partners/staging"
            icon={<ContactMailIcon />}
            label="HXC Partner Creation"
            isActive={location.pathname.startsWith("/hxc-partners")}
          />
        )}
        {showMasterData && (
          <NavItem
            to="/master-data"
            icon={<StorageIcon />}
            label="Master Data"
            isActive={location.pathname.startsWith("/master-data")}
          />
        )}
        {showCompSets && (
          <NavItem
            to="/comp-sets"
            icon={<CompareArrowsIcon />}
            label="Comp Sets"
            isActive={location.pathname.startsWith("/comp-sets")}
          />
        )}
        {showAccountAccess && (
          <NavItem
            to="/data-access-control"
            icon={<SecurityIcon />}
            label="Data Access Control"
            isActive={location.pathname.startsWith("/data-access-control")}
          />
        )}
        {showProgramAccess && (
          <NavItem
            to="/program-access"
            icon={<AccountTreeIcon />}
            label="Program Leader Center"
            isActive={location.pathname.startsWith("/program-access")}
          />
        )}
        {showChangeLog && (
          <NavItem
            to="/change-log"
            icon={<HistoryIcon />}
            label="Change Log"
            isActive={location.pathname.startsWith("/change-log")}
          />
        )}
        {showAlerts && (
          <NavItem
            to="/alerts"
            icon={<WarningRoundedIcon />}
            label="Alerts"
            isActive={
              location.pathname === "/alerts" ||
              location.pathname.startsWith("/alerts")
            }
          />
        )}        
        {showParticipants && (
          <NavItem
            to="/participants"
            icon={<PersonSearchIcon />}
            label="Participants"
            isActive={
              location.pathname === "/participants" ||
              location.pathname.startsWith("/participants")
            }
          />
        )}

        {/* Tools Section */}
        <Divider sx={{ my: 1, borderColor: "rgba(255, 255, 255, 0.2)" }} />

        <NavItem
          to="/region-sync"
          icon={<SyncIcon />}
          label="Region Sync"
          isActive={
            location.pathname === "/region-sync" ||
            location.pathname.startsWith("/region-sync")
          }
        />
      </List>
    </Box>
  );
};

export default LeftNav;
